export interface RoundRobinPlayer {
  userId: string;
  seed: number;
}

export interface RoundRobinMatchResult {
  aScore: number;
  bScore: number;
  winnerUserId: string;
  completedAt: string;
  playerAPoints?: number;
  playerBPoints?: number;
}

export interface RoundRobinMatch {
  matchIndex: number;
  round: number;
  playerA: RoundRobinPlayer;
  playerB: RoundRobinPlayer;
  result: RoundRobinMatchResult | null;
}

export const BYE_USER_ID = 'BYE';

/**
 * Generates a round-robin schedule using the circle method.
 * Every player faces every other player exactly once.
 * BYE entries are emitted explicitly so the UI can show off-rounds.
 *
 * @param players list of players that includes a valid seed for ordering
 */
export function generateRoundRobinSchedule(
  players: ReadonlyArray<RoundRobinPlayer>
): RoundRobinMatch[] {
  const normalized = players.map(p => ({
    userId: p.userId,
    seed: Number.isFinite(p.seed) ? p.seed : 0
  }));

  if (normalized.length < 2) {
    throw new Error('Need at least 2 players for round-robin');
  }

  const roster = [...normalized].sort((a, b) => a.seed - b.seed);

  if (roster.length % 2 === 1) {
    roster.push({ userId: BYE_USER_ID, seed: 0 });
  }

  const numPlayers = roster.length;
  const numRounds = numPlayers - 1;
  const matchesPerRound = numPlayers / 2;
  const schedule: RoundRobinMatch[] = [];

  let matchIndex = 1;

  for (let round = 0; round < numRounds; round++) {
    for (let slot = 0; slot < matchesPerRound; slot++) {
      let home = (round + slot) % (numPlayers - 1);
      let away = (numPlayers - 1 - slot + round) % (numPlayers - 1);

      if (slot === 0) {
        away = numPlayers - 1;
      }

      const playerA = roster[home];
      const playerB = roster[away];

      schedule.push({
        matchIndex: matchIndex++,
        round: round + 1,
        playerA,
        playerB,
        result: null
      });
    }
  }

  return schedule;
}
